document.documentElement.classList.remove('no-js');

const shopRoot = document.body?.dataset.shopRoot || window.Shopify?.routes?.root || '/';
const shopRoute = (path) => `${shopRoot}${String(path).replace(/^\//, '')}`;
const freeShippingThreshold = Number(document.body?.dataset.freeShippingThreshold || 5000);
let lastCartTrigger = null;

const escapeHtml = (value = '') => String(value).replace(/[&<>'"]/g, (character) => ({
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  "'": '&#039;',
  '"': '&quot;',
}[character]));

const money = (cents, format) => {
  const amount = (Number(cents || 0) / 100).toFixed(2);
  return (format || '${{amount}}')
    .replace(/{{\s*amount\s*}}/, amount)
    .replace(/{{\s*amount_no_decimals\s*}}/, Math.round(Number(cents || 0) / 100).toString())
    .replace(/{{\s*amount_with_comma_separator\s*}}/, amount.replace('.', ','));
};

async function requestJson(url, options = {}) {
  const requestOptions = { ...options, headers: { Accept: 'application/json', ...(options.headers || {}) } };
  const response = await fetch(url, requestOptions);
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) throw new Error(payload.description || payload.message || 'Something went wrong. Please try again.');
  return payload;
}

function openCart(trigger = document.activeElement) {
  const drawer = document.getElementById('RrCartDrawer');
  if (!drawer) return;
  lastCartTrigger = trigger instanceof HTMLElement ? trigger : null;
  drawer.classList.add('open');
  drawer.setAttribute('aria-hidden', 'false');
  document.body.style.overflow = 'hidden';
  drawer.querySelector('.drawer-head button')?.focus();
}

function closeCart() {
  const drawer = document.getElementById('RrCartDrawer');
  if (!drawer) return;
  drawer.classList.remove('open');
  drawer.setAttribute('aria-hidden', 'true');
  document.body.style.overflow = '';
  lastCartTrigger?.focus();
  lastCartTrigger = null;
}

function cartItemMarkup(item, index) {
  const variantTitle = item.variant_title && item.variant_title !== 'Default Title'
    ? `<span>${escapeHtml(item.variant_title)}</span>`
    : '';
  const sellingPlan = item.selling_plan_allocation?.selling_plan?.name
    ? `<span>${escapeHtml(item.selling_plan_allocation.selling_plan.name)}</span>`
    : '';
  const brandedImage = item.handle === 'mineral-reset'
    ? document.body.dataset.starterImage
    : item.handle === 'mineral-reset-refill'
      ? document.body.dataset.refillImage
      : item.image;
  const image = brandedImage
    ? `<img src="${escapeHtml(brandedImage)}" alt="${escapeHtml(item.product_title)}">`
    : '';

  return `<div class="cart-item">
    ${image}
    <div>
      <strong>${escapeHtml(item.product_title)}</strong>
      ${variantTitle}
      ${sellingPlan}
      <label class="rr-cart-qty">Quantity
        <input data-cart-line="${index + 1}" aria-label="Quantity for ${escapeHtml(item.product_title)}" type="number" min="0" inputmode="numeric" value="${Number(item.quantity)}">
      </label>
    </div>
    <strong>${money(item.final_line_price, window.rrMoneyFormat)}</strong>
  </div>`;
}

function shippingProgressMarkup(cart) {
  const hasStarter = cart.items.some((item) => item.handle === 'mineral-reset');
  const hasSubscription = cart.items.some((item) => Boolean(item.selling_plan_allocation));
  const thresholdReached = freeShippingThreshold > 0 && cart.total_price >= freeShippingThreshold;
  const unlocked = hasStarter || hasSubscription || thresholdReached;
  const progress = unlocked || freeShippingThreshold <= 0
    ? 100
    : Math.min(100, Math.round((cart.total_price / freeShippingThreshold) * 100));
  const remaining = Math.max(0, freeShippingThreshold - cart.total_price);
  const message = unlocked
    ? 'Free shipping unlocked'
    : `${money(remaining, window.rrMoneyFormat)} away from free shipping`;
  const detail = unlocked
    ? 'Starter Systems and subscriptions ship free.'
    : `Free shipping begins at ${money(freeShippingThreshold, window.rrMoneyFormat)}.`;

  return `<div class="rr-shipping-meter${unlocked ? ' unlocked' : ''}">
    <div><strong>${message}</strong><span>${progress}%</span></div>
    <i aria-hidden="true"><b style="width:${progress}%"></b></i>
    <small>${detail}</small>
  </div>`;
}

function cartRecommendationMarkup(cart) {
  const refillOnly = cart.items.length > 0 && cart.items.every((item) => item.handle === 'mineral-reset-refill');
  const hasSubscription = cart.items.some((item) => Boolean(item.selling_plan_allocation));
  if (!refillOnly || hasSubscription) return '';
  return `<a class="rr-cart-recommendation" href="${shopRoute('products/mineral-reset')}"><span><strong>Need the reusable applicator?</strong><small>Start with the complete Starter System.</small></span><b>View starter →</b></a>`;
}

function syncPurchaseDisplay(form, selected) {
  if (!form || !selected) return;
  form.querySelectorAll('[data-mobile-price]').forEach((node) => { node.textContent = selected.dataset.price || ''; });
  form.querySelectorAll('[data-mobile-plan]').forEach((node) => { node.textContent = selected.dataset.label || ''; });
}

async function refreshCart(openDrawer = false, trigger = document.activeElement) {
  const cart = await requestJson(shopRoute('cart.js'));
  document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = cart.item_count; });
  const body = document.querySelector('[data-cart-drawer-body]');

  if (body) {
    if (!cart.items.length) {
      body.innerHTML = `<div class="empty-bag-state"><p>Your bag is empty.</p><a class="primary-cta" href="${shopRoute('collections/all')}">Continue shopping</a></div>`;
    } else {
      body.innerHTML = `<div class="rr-cart-items">${cart.items.map(cartItemMarkup).join('')}</div>
        ${shippingProgressMarkup(cart)}
        ${cartRecommendationMarkup(cart)}
        <div class="drawer-spacer"></div>
        <div class="drawer-perks"><span>✓ 60-day First Reset Guarantee</span><span>✓ Secure Shopify checkout</span><span>✓ Subscription orders can be skipped, paused or cancelled</span></div>
        <div class="drawer-total"><span>Subtotal</span><strong>${money(cart.total_price, window.rrMoneyFormat)}</strong></div>
        <p class="drawer-note">Taxes and shipping calculated at checkout.</p>
        <a class="primary-cta checkout" href="${shopRoute('checkout')}">Continue to secure checkout</a>`;
    }
  }

  if (openDrawer) openCart(trigger);
  return cart;
}

document.addEventListener('click', async (event) => {
  const cartOpen = event.target.closest('[data-cart-open]');
  const cartClose = event.target.closest('[data-cart-close]');
  const addButton = event.target.closest('[data-product-submit]');
  const carouselButton = event.target.closest('[data-scroll-target]');
  const thumb = event.target.closest('[data-gallery-thumb]');
  const qtyMinus = event.target.closest('[data-qty-minus]');
  const qtyPlus = event.target.closest('[data-qty-plus]');

  if (cartOpen) {
    event.preventDefault();
    try { await refreshCart(true, cartOpen); } catch (error) { openCart(cartOpen); }
  }
  if (cartClose) closeCart();

  if (carouselButton) {
    const target = document.getElementById(carouselButton.dataset.scrollTarget);
    if (target) target.scrollBy({ left: target.clientWidth * Number(carouselButton.dataset.direction || 1) * 0.9, behavior: 'smooth' });
  }

  if (thumb) {
    const main = document.querySelector('[data-gallery-main]');
    if (main) {
      main.removeAttribute('srcset');
      main.removeAttribute('sizes');
      main.src = thumb.dataset.image;
      main.alt = thumb.dataset.alt || '';
    }
    document.querySelectorAll('[data-gallery-thumb]').forEach((item) => item.classList.toggle('active', item === thumb));
  }

  if (qtyMinus || qtyPlus) {
    const input = event.target.closest('.quantity')?.querySelector('input[name="quantity"]');
    if (input) input.value = Math.max(1, Number(input.value || 1) + (qtyPlus ? 1 : -1));
  }

  if (addButton) {
    event.preventDefault();
    const form = addButton.closest('form');
    if (!form || addButton.disabled) return;
    const status = form.querySelector('[data-product-status]');
    if (status) status.textContent = '';
    addButton.disabled = true;
    addButton.setAttribute('aria-busy', 'true');

    try {
      await requestJson(shopRoute('cart/add.js'), { method: 'POST', body: new FormData(form) });
      await refreshCart(true, addButton);
    } catch (error) {
      if (status) status.textContent = error.message;
    } finally {
      addButton.disabled = false;
      addButton.removeAttribute('aria-busy');
    }
  }
});

document.addEventListener('change', async (event) => {
  if (event.target.matches('[data-cart-line]')) {
    const input = event.target;
    input.disabled = true;
    try {
      await requestJson(shopRoute('cart/change.js'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ line: Number(input.dataset.cartLine), quantity: Math.max(0, Number(input.value || 0)) }),
      });
      await refreshCart(false);
    } catch (error) {
      await refreshCart(false).catch(() => {});
    } finally {
      input.disabled = false;
    }
  }

  if (event.target.matches('[data-purchase-option]')) {
    const form = event.target.closest('form');
    const sellingPlan = form?.querySelector('[name="selling_plan"]');
    const selected = event.target;
    if (sellingPlan) {
      sellingPlan.value = selected.dataset.sellingPlan || '';
      sellingPlan.disabled = !selected.dataset.sellingPlan;
    }
    form?.querySelectorAll('.purchase-options label, .refill-options label').forEach((label) => label.classList.toggle('selected', label.contains(selected)));
    syncPurchaseDisplay(form, selected);
  }
});

document.addEventListener('keydown', (event) => {
  if (event.key === 'Escape') closeCart();
});

window.addEventListener('DOMContentLoaded', () => {
  window.rrMoneyFormat = document.body.dataset.moneyFormat || '${{amount}}';
  document.querySelectorAll('[data-product-form]').forEach((form) => syncPurchaseDisplay(form, form.querySelector('[data-purchase-option]:checked')));
  refreshCart(false).catch(() => {});
});
